import React, { useEffect, useState } from 'react';
import { X, ArrowRight, MapPin, Star, ShieldCheck, Phone, Sparkles, Loader2, Truck } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { routeApi } from '../lib/api';
import { toast } from '../hooks/use-toast';

const formatINR = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);
const timeAgo = (m) => m < 60 ? `${m}m ago` : m < 1440 ? `${Math.floor(m/60)}h ago` : `${Math.floor(m/1440)}d ago`;

const demoDestinations = ['Delhi, DL', 'Mumbai, MH', 'Bengaluru, KA', 'Chennai, TN', 'Kolkata, WB', 'Hyderabad, TS', 'Ahmedabad, GJ', 'Pune, MH', 'Jaipur, RJ', 'Lucknow, UP', 'Nagpur, MH', 'Indore, MP'];
const demoMats = ['Cement', 'Steel & TMT Bars', 'Textiles', 'FMCG', 'Auto Parts', 'Electronics', 'Chemicals'];
const demoPosters = ['Reliance Freight', 'TCI Express', 'VRL Logistics', 'Safexpress', 'AllCargo Logistics', 'Bharat Movers'];
const rnd = (arr) => arr[Math.floor(Math.random() * arr.length)];
const rndInt = (a, b) => Math.floor(Math.random() * (b - a + 1)) + a;

const makeLocalDemo = (completed) => {
  const fromCity = completed.destination;
  const homeCity = completed.origin;
  const dests = [homeCity, ...demoDestinations.filter(d => d !== fromCity && d !== homeCity).slice(0, 4)];
  const matches = dests.map((dst, i) => {
    const rate = rndInt(38000, 92000);
    const dist = rndInt(400, 1500);
    return {
      id: `LD${rndInt(10000, 99999)}`,
      origin: fromCity,
      destination: dst,
      material: rnd(demoMats),
      weight: `${rndInt(8, 25)} T`,
      truck_type: '32ft Multi Axle',
      truck_code: 'TR-32',
      rate,
      distance: dist,
      poster_company: rnd(demoPosters),
      poster_name: 'Auto-matched',
      poster_phone: '+91 98765 43210',
      payment_terms: rnd(['Advance 30%', 'Advance 50%', 'On Delivery']),
      verified: true,
      match_score: (dst === homeCity ? 100 : 0) + rate / dist,
    };
  }).sort((a, b) => b.match_score - a.match_score);
  return { from_city: fromCity, preferred_return: homeCity, matches };
};

export default function ReturnLoadMatch({ open, onClose, completedLoad }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !completedLoad) return;
    setLoading(true);
    routeApi.returnMatches(completedLoad.id).then(r => {
      if (r && r.matches && r.matches.length > 0) setData(r);
      else setData(makeLocalDemo(completedLoad));
    }).catch(() => setData(makeLocalDemo(completedLoad))).finally(() => setLoading(false));
  }, [open, completedLoad]);

  if (!open || !completedLoad) return null;

  const book = (match) => {
    toast({ title: 'Return load booked!', description: `${match.origin.split(',')[0]} → ${match.destination.split(',')[0]} — ${formatINR(match.rate)}. Poster will be notified.` });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[110] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-2xl sm:rounded-2xl max-h-[95vh] overflow-y-auto">
        <div className="sticky top-0 z-10 bg-gradient-to-r from-[#00A651] to-[#00c060] text-white p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <p className="font-bold">Return Load Match</p>
                <p className="text-[11px] text-white/80">Auto-suggested to prevent empty return</p>
              </div>
            </div>
            <button onClick={onClose} className="p-1 rounded hover:bg-white/10"><X className="w-5 h-5" /></button>
          </div>
          <div className="mt-3 bg-white/10 rounded-lg p-2 flex items-center justify-between text-xs">
            <span>Delivered:</span>
            <span className="font-mono">{completedLoad.id} • {completedLoad.origin?.split(',')[0]} → {completedLoad.destination?.split(',')[0]}</span>
          </div>
        </div>

        <div className="p-4">
          <div className="flex items-center gap-2 mb-4 text-sm text-gray-600">
            <MapPin className="w-4 h-4 text-[#00A651]" />
            <span>You're at <span className="font-bold text-[#0B2545]">{data?.from_city?.split(',')[0] || completedLoad.destination?.split(',')[0]}</span>.</span>
            {data?.preferred_return && <span>Best return: <span className="font-bold text-[#0B2545]">{data.preferred_return.split(',')[0]}</span>.</span>}
          </div>

          {loading ? (
            <div className="py-12 text-center text-gray-400"><Loader2 className="w-8 h-8 animate-spin mx-auto" /></div>
          ) : !data || data.matches.length === 0 ? (
            <div className="py-12 text-center text-gray-500">
              <Truck className="w-12 h-12 mx-auto text-gray-300 mb-2" />
              <p>No return loads available from this city right now.</p>
              <p className="text-xs mt-1">We'll notify you as soon as one is posted.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {data.matches.map((m, i) => (
                <div key={m.id} className={`border rounded-xl p-3 transition-all ${i === 0 ? 'border-[#00A651] bg-[#00A651]/5' : 'border-gray-100'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[10px] text-gray-500">{m.id}</span>
                      {i === 0 && <Badge className="bg-[#00A651] text-white border-0 text-[9px]"><Sparkles className="w-2.5 h-2.5 mr-0.5" />BEST MATCH</Badge>}
                      {m.destination === data.preferred_return && <Badge className="bg-blue-50 text-blue-600 border-0 text-[9px]">HOME ROUTE</Badge>}
                      {m.verified && <Badge className="bg-[#00A651]/10 text-[#00A651] border-0 text-[9px]"><ShieldCheck className="w-2.5 h-2.5 mr-0.5" />Verified</Badge>}
                    </div>
                    <p className="font-bold text-[#00A651]">{formatINR(m.rate)}</p>
                  </div>
                  <div className="flex items-center gap-2 text-sm font-semibold text-[#0B2545] mb-2">
                    <span>{m.origin?.split(',')[0]}</span>
                    <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
                    <span>{m.destination?.split(',')[0]}</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 text-[10px] mb-2">
                    <span className="bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded">{m.material}</span>
                    <span className="bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded">{m.weight}</span>
                    <span className="bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded">{m.truck_type}</span>
                    <span className="bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded">{m.distance} km</span>
                    <span className="bg-[#FFB800]/15 text-[#B37800] px-1.5 py-0.5 rounded">{m.payment_terms}</span>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                    <div className="text-[10px] text-gray-600">
                      <p className="font-semibold text-[#0B2545] text-xs">{m.poster_company}</p>
                      <p>₹{Math.round(m.rate / m.distance)}/km • score {m.match_score}</p>
                    </div>
                    <div className="flex gap-1.5">
                      <Button size="sm" variant="outline" className="h-8 px-2" onClick={() => toast({ title: 'Calling poster', description: m.poster_phone })}><Phone className="w-3 h-3" /></Button>
                      <Button size="sm" className="h-8 bg-[#00A651] hover:bg-[#008a43] text-white" onClick={() => book(m)}>Book Now</Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-4 pt-4 border-t border-gray-100 flex gap-2">
            <Button variant="outline" className="flex-1" onClick={onClose}>Skip for now</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
